import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getConvQuota } from '../chatv1/transport'
import { buyGroupPosts } from '../transport/relay'
import { Icon } from '../ui/Icon'
import { groupErrorText } from './groupText'
import { POSTS_PER_SPARK, resetsIn } from './limitText'
import './groups.css'

/**
 * WHAT I HAVE LEFT TO SAY HERE, under a group's chat — owner, 2026-10-20: 使用者發文的限制跟它的 Agent 是共享的 …
 * 使用者如果想要額外發文, 可以使用他的 Spark. 每一個 Spark 可以增加發文二十次.
 *
 * Drawn only where a limit holds the viewer (a group's room with a posting limit, and not its owner). It reads the
 * chat stack's own count (quota.ts) and reads it again after each message in the room — mine, or one my agent sent
 * — so the number follows the posts. Out of posts, it offers the Spark.
 */
const QUOTA_KEY = (conv: string) => ['conv-quota', conv] as const

export function GroupQuotaBar({ conv, gid, bump }: { conv: string; gid: string; bump: number }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: QUOTA_KEY(conv), queryFn: () => getConvQuota(conv), staleTime: 0, retry: false })
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  // Every new message in the room may have been one of mine (or my agent's): count again — on a change, not on
  // the first draw, which the query itself has just read.
  const v = q.data
  const held = !!v && v.limit !== null && !v.exempt
  const seen = useRef(bump)
  useEffect(() => {
    if (bump === seen.current) return
    seen.current = bump
    if (held) void qc.invalidateQueries({ queryKey: QUOTA_KEY(conv) })
  }, [bump, conv, qc, held])
  // A new day's allowance arrives at midnight UTC; read it then rather than at the next message.
  const resetsAt = v?.resets_at ?? 0
  useEffect(() => {
    if (!held || !resetsAt) return
    const ms = Math.min(24 * 3600_000, Math.max(1000, resetsAt * 1000 - Date.now() + 2000))
    const t = setTimeout(() => void qc.invalidateQueries({ queryKey: QUOTA_KEY(conv) }), ms)
    return () => clearTimeout(t)
  }, [held, resetsAt, conv, qc])

  if (!v || v.limit === null || v.exempt) return null
  if (v.limit === 0) {
    return (
      <div className="grp-quota out" role="status">
        <span className="grp-quota-text">Only the group’s owner posts here.</span>
      </div>
    )
  }
  const today = Math.max(0, v.limit - v.used)
  const left = today + v.extra
  const buy = async () => {
    setBusy(true)
    setNote(null)
    try {
      const r = await buyGroupPosts(gid)
      setNote(`${r.added} more posts — ${r.sparks} Spark${r.sparks === 1 ? '' : 's'} left.`)
      await qc.invalidateQueries({ queryKey: QUOTA_KEY(conv) })
      // Every count of my Sparks on screen: the Invite sheet's and my profile page's.
      void qc.invalidateQueries({ queryKey: ['sparks'] })
      void qc.invalidateQueries({ queryKey: ['profile-card'] })
    } catch (e) {
      setNote(groupErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={`grp-quota${left === 0 ? ' out' : ''}`} role="status" aria-live="polite">
      <span className="grp-quota-text">
        {left > 0 ? (
          <>
            <b>{left}</b> post{left === 1 ? '' : 's'} left
            {v.extra > 0 ? ` (${v.extra} bought)` : ' today'} · your agents share them
          </>
        ) : (
          <>Today’s {v.limit} post{v.limit === 1 ? ' is' : 's are'} used · new ones {resetsIn(v.resets_at)}</>
        )}
        {note && <span className="grp-quota-note">{note}</span>}
      </span>
      {left <= Math.min(3, v.limit) && (
        <button type="button" className="fr-btn ok grp-quota-buy" disabled={busy} onClick={() => void buy()}>
          <Icon name="auto_awesome" size={14} />
          {busy ? 'Spending…' : `1 Spark → ${POSTS_PER_SPARK} posts`}
        </button>
      )}
    </div>
  )
}
